import React from "react"
import { VStack } from "@chakra-ui/react"
import { MedicalInfo } from "../../types/MedicalInfo"
import * as MedicalOptions from "../../utils/MedicalInfoOptions"
import { AnswerList, Answer } from "./Answers"
import { capitalizeFirstLetter, getOptionTitle } from "../../utils/AnswerUtils"
import { MedicalInfoAnswerKey, MedicalInfoQuestionKey } from "../../utils/RoutingInfoQuestionKey"
import { RoutingInfoQuestion } from "./RoutingInfoQuestions"
import { hasMentalHealthScreenerAnswer, MENTAL_HEALTH_ITEMS, MENTAL_HEALTH_PREFIX } from "./mentalHealthScreenerConfig"

export function MentalHealthScreenerAnswers({ medicalInfo }: { medicalInfo: MedicalInfo }): JSX.Element {
  return (
    <VStack align={"stretch"} spacing={2}>
      {MENTAL_HEALTH_ITEMS.map(({ field, label }) => {
        const value: string = medicalInfo?.[field]
        if (value == null) return null
        const optionTitle = getOptionTitle(MedicalOptions.MentalHealthScreenerOptions, value)
        return (
          <Answer key={field}>
            {label} — {optionTitle ?? value}
          </Answer>
        )
      })}
    </VStack>
  )
}

type HeightWeightProps = {
  height: number
  heightUnits: string
  weight: number
  weightUnits: string
}

function HeightWeight({ height, heightUnits, weight, weightUnits }: HeightWeightProps): JSX.Element {
  const heightDisplay = height && heightUnits == "IN" ? `${Math.floor(height / 12)}'${height % 12}"` : null
  const weightDisplay = weight && weightUnits == "LB" ? `${weight}lb` : null
  return (
    <>
      <Answer>{heightDisplay}</Answer>
      <Answer>{weightDisplay}</Answer>
    </>
  )
}

export function getAfabOnlyMedicalQuestions(medicalInfo: MedicalInfo): RoutingInfoQuestion[] {
  return [
    {
      questionKey: MedicalInfoQuestionKey.PregnancyMetWithObgyn,
      answerKey: MedicalInfoAnswerKey.PregnancyMetWithObgyn,
      hasAnswer: medicalInfo?.pregnancyMetWithObgyn !== null,
      question: "Have you met with an obstetrician (OB/GYN) since becoming pregnant?",
      answer: <>{capitalizeFirstLetter(medicalInfo?.pregnancyMetWithObgyn)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.PregnancyPastFertilityTreatment,
      answerKey: MedicalInfoAnswerKey.PregnancyPastFertilityTreatment,
      hasAnswer: medicalInfo?.pregnancyPastFertilityTreatment !== null,
      question: "Have you received any type of fertility treatment in the past?",
      answer: <>{capitalizeFirstLetter(medicalInfo?.pregnancyPastFertilityTreatment)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.MenopauseDiagnosis,
      answerKey: MedicalInfoAnswerKey.MenopauseDiagnosis,
      hasAnswer: medicalInfo?.menopauseDiagnosis !== null,
      question: "Have you been diagnosed with menopause?",
      answer: <>{capitalizeFirstLetter(medicalInfo?.menopauseDiagnosis)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.MenopauseDiagnosisDetails,
      answerKey: MedicalInfoAnswerKey.MenopauseDiagnosisDetails,
      hasAnswer: medicalInfo?.menopauseDiagnosisDetails !== null,
      question: "Select all that apply. (Menopause diagnosis followup)",
      answer: (
        <AnswerList
          optionTitles={MedicalOptions.MenopauseDiagnosisDetails}
          answers={medicalInfo?.menopauseDiagnosisDetails}
        />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.MenopauseSymptoms,
      answerKey: MedicalInfoAnswerKey.MenopauseSymptoms,
      hasAnswer: medicalInfo?.menopauseSymptoms !== null,
      question: "Have you noticed any of the following symptoms?",
      answer: <AnswerList optionTitles={MedicalOptions.MenopauseSymptoms} answers={medicalInfo?.menopauseSymptoms} />
    },
    {
      questionKey: MedicalInfoQuestionKey.CyclesAreRegular,
      answerKey: MedicalInfoAnswerKey.CyclesAreRegular,
      hasAnswer: medicalInfo?.cyclesAreRegular !== null,
      question: "Do you have regular menstrual cycles?",
      answer: <>{getOptionTitle(MedicalOptions.CyclesAreRegularOptions, medicalInfo?.cyclesAreRegular)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.CycleDetails,
      answerKey: MedicalInfoAnswerKey.CycleDetails,
      hasAnswer: medicalInfo?.cycleDetails !== null,
      question: "Select all that apply. (Irregular menstrual cycles followup)",
      answer: <AnswerList optionTitles={MedicalOptions.CycleDetails} answers={medicalInfo?.cycleDetails} />
    },
    {
      questionKey: MedicalInfoQuestionKey.PeriodsPainful,
      answerKey: MedicalInfoAnswerKey.PeriodsPainful,
      hasAnswer: medicalInfo?.periodsPainful !== null,
      question: "Are your periods so painful that they make you miss work or activities?",
      answer: <>{capitalizeFirstLetter(medicalInfo?.periodsPainful)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.PeriodsPainfulDetails,
      answerKey: MedicalInfoAnswerKey.PeriodsPainfulDetails,
      hasAnswer: medicalInfo?.periodsPainfulDetails !== null,
      question: "Do you also experience any of the following around your period?",
      answer: (
        <AnswerList optionTitles={MedicalOptions.PeriodsPainfulDetails} answers={medicalInfo?.periodsPainfulDetails} />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.PregnantPreviously,
      answerKey: MedicalInfoAnswerKey.PregnantPreviously,
      hasAnswer: medicalInfo?.pregnantPreviously !== null,
      question: "Have you ever been pregnant previously?",
      answer: <>{capitalizeFirstLetter(medicalInfo?.pregnantPreviously)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.PregnantPreviouslyDetails,
      answerKey: MedicalInfoAnswerKey.PregnantPreviouslyDetails,
      hasAnswer: medicalInfo?.pregnantPreviouslyDetails !== null,
      question: "Have you experienced any of the following?",
      answer: (
        <>
          <AnswerList
            optionTitles={MedicalOptions.PregnantPreviouslyDetails}
            answers={medicalInfo?.pregnantPreviouslyDetails}
          />
          {medicalInfo?.pregnantPreviouslyDetailsOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.MedicalHistory,
      answerKey: MedicalInfoAnswerKey.FemaleMedicalHistory,
      hasAnswer: medicalInfo?.femaleMedicalHistory !== null,
      question: "Do you have any of the following in your medical history?",
      answer: (
        <AnswerList optionTitles={MedicalOptions.MedicalHistoryOptions} answers={medicalInfo?.femaleMedicalHistory} />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.PelvicSurgeryDetails,
      answerKey: MedicalInfoAnswerKey.PelvicSurgeryDetails,
      hasAnswer: medicalInfo?.pelvicSurgeryDetails !== null,
      question: "What type of pelvic or abdominal surgery did you have?",
      answer: (
        <AnswerList optionTitles={MedicalOptions.PelvicSurgeryDetails} answers={medicalInfo?.pelvicSurgeryDetails} />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.Conditions,
      answerKey: MedicalInfoAnswerKey.FemaleConditions,
      hasAnswer: medicalInfo?.femaleConditions !== null,
      question: "Do you have any of these conditions?",
      answer: (
        <>
          <AnswerList optionTitles={MedicalOptions.ConditionsOptions} answers={medicalInfo?.femaleConditions} />
          {medicalInfo?.conditionsOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.FamilyConditions,
      answerKey: MedicalInfoAnswerKey.FemaleFamilyConditions,
      hasAnswer: medicalInfo?.femaleFamilyConditions !== null,
      question: "Has a member of your immediate family experienced any of these conditions?",
      answer: (
        <>
          <AnswerList
            optionTitles={MedicalOptions.FamilyConditionsOptions}
            answers={medicalInfo?.femaleFamilyConditions}
          />
          {medicalInfo?.familyConditionsOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.CurrentlyTaking,
      answerKey: MedicalInfoAnswerKey.FemaleCurrentlyTaking,
      hasAnswer: medicalInfo?.femaleCurrentlyTaking !== null,
      question: "Are you currently taking any of the following?",
      answer: (
        <>
          <AnswerList
            optionTitles={MedicalOptions.CurrentlyTakingOptions}
            answers={medicalInfo?.femaleCurrentlyTaking}
          />
          {medicalInfo?.currentlyTakingOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.ExperiencingSymptoms,
      answerKey: MedicalInfoAnswerKey.FemaleExperiencingSymptoms,
      hasAnswer: medicalInfo?.femaleExperiencingSymptoms !== null,
      question: "Are you experiencing any of the following?",
      answer: (
        <AnswerList
          optionTitles={MedicalOptions.ExperiencingSymptomsOptions}
          answers={medicalInfo?.femaleExperiencingSymptoms}
        />
      )
    }
  ]
}

export function getAmabOnlyMedicalQuestions(medicalInfo: MedicalInfo): RoutingInfoQuestion[] {
  return [
    {
      questionKey: MedicalInfoQuestionKey.BiologicalChildren,
      answerKey: MedicalInfoAnswerKey.BiologicalChildren,
      hasAnswer: medicalInfo?.biologicalChildren !== null,
      question: "Have you had genetically related children before?",
      answer: <>{capitalizeFirstLetter(medicalInfo?.biologicalChildren)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.LowTestosteroneSymptoms,
      answerKey: MedicalInfoAnswerKey.LowTestosteroneSymptoms,
      hasAnswer: medicalInfo?.lowTestosteroneSymptoms !== null,
      question: "Have you noticed any of the following symptoms?",
      answer: (
        <AnswerList
          optionTitles={MedicalOptions.LowTestosteroneSymptomsOptions}
          answers={medicalInfo?.lowTestosteroneSymptoms}
        />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.MedicalHistory,
      answerKey: MedicalInfoAnswerKey.MaleMedicalHistory,
      hasAnswer: medicalInfo?.maleMedicalHistory !== null,
      question: "Do you have any of the following in your medical history?",
      answer: (
        <AnswerList optionTitles={MedicalOptions.MedicalHistoryOptions} answers={medicalInfo?.maleMedicalHistory} />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.Conditions,
      answerKey: MedicalInfoAnswerKey.MaleConditions,
      hasAnswer: medicalInfo?.maleConditions !== null,
      question: "Do you have any of these conditions?",
      answer: (
        <>
          <AnswerList optionTitles={MedicalOptions.ConditionsOptions} answers={medicalInfo?.maleConditions} />
          {medicalInfo?.conditionsOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.FamilyConditions,
      answerKey: MedicalInfoAnswerKey.MaleFamilyConditions,
      hasAnswer: medicalInfo?.maleFamilyConditions !== null,
      question: "Has a member of your immediate family experienced any of these conditions?",
      answer: (
        <>
          <AnswerList
            optionTitles={MedicalOptions.FamilyConditionsOptions}
            answers={medicalInfo?.maleFamilyConditions}
          />
          {medicalInfo?.familyConditionsOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.CurrentlyTaking,
      answerKey: MedicalInfoAnswerKey.MaleCurrentlyTaking,
      hasAnswer: medicalInfo?.maleCurrentlyTaking !== null,
      question: "Are you currently taking any of the following?",
      answer: (
        <>
          <AnswerList optionTitles={MedicalOptions.CurrentlyTakingOptions} answers={medicalInfo?.maleCurrentlyTaking} />
          {medicalInfo?.currentlyTakingOther}
        </>
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.ExperiencingSymptoms,
      answerKey: MedicalInfoAnswerKey.MaleExperiencingSymptoms,
      hasAnswer: medicalInfo?.maleExperiencingSymptoms !== null,
      question: "Are you experiencing any of the following?",
      answer: (
        <AnswerList
          optionTitles={MedicalOptions.ExperiencingSymptomsOptions}
          answers={medicalInfo?.maleExperiencingSymptoms}
        />
      )
    }
  ]
}

export function getPregnancyMedicalQuestions(medicalInfo: MedicalInfo): RoutingInfoQuestion[] {
  return [
    {
      questionKey: MedicalInfoQuestionKey.PregnancyBabyFeedingPlan,
      answerKey: MedicalInfoAnswerKey.PregnancyBabyFeedingPlan,
      hasAnswer: medicalInfo?.pregnancyBabyFeedingPlan !== null,
      question: "How do you plan to feed your baby?",
      answer: <>{getOptionTitle(MedicalOptions.BabyFeedingOptions, medicalInfo?.pregnancyBabyFeedingPlan)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.PregnancyReturnToWorkPlan,
      answerKey: MedicalInfoAnswerKey.PregnancyReturnToWorkPlan,
      hasAnswer: medicalInfo?.pregnancyReturnToWorkPlan !== null,
      question:
        "If you are currently employed part or full time, do you intend to return to work after birth/parental leave?",
      answer: <>{getOptionTitle(MedicalOptions.PregnancyReturnToWorkOptions, medicalInfo?.pregnancyReturnToWorkPlan)}</>
    }
  ]
}

export function getPostpartumMedicalQuestions(medicalInfo: MedicalInfo): RoutingInfoQuestion[] {
  return [
    {
      questionKey: MedicalInfoQuestionKey.PostpartumBabyFeedingStatus,
      answerKey: MedicalInfoAnswerKey.PostpartumBabyFeedingStatus,
      hasAnswer: medicalInfo?.postpartumBabyFeedingStatus !== null,
      question: "If you are parenting an infant, how are you feeding your baby?",
      answer: <>{getOptionTitle(MedicalOptions.BabyFeedingOptions, medicalInfo?.postpartumBabyFeedingStatus)}</>
    },
    {
      questionKey: MedicalInfoQuestionKey.PostpartumReturnToWorkStatus,
      answerKey: MedicalInfoAnswerKey.PostpartumReturnToWorkStatus,
      hasAnswer: medicalInfo?.postpartumReturnToWorkStatus !== null,
      question: "What is your employment and return-to-work status?",
      answer: (
        <>{getOptionTitle(MedicalOptions.PostpartumReturnToWorkOptions, medicalInfo?.postpartumReturnToWorkStatus)}</>
      )
    }
  ]
}

// Questions shared by more than one flow - don't need an answer key as they don't help identify the flow
export function getSharedMedicalQuestions(medicalInfo: MedicalInfo): RoutingInfoQuestion[] {
  return [
    {
      questionKey: MedicalInfoQuestionKey.SmokeDrink,
      hasAnswer: medicalInfo?.smokeDrink !== null,
      question: "Select any that apply.",
      answer: <AnswerList optionTitles={MedicalOptions.SmokeDrinkOptions} answers={medicalInfo?.smokeDrink} />
    },
    {
      questionKey: MedicalInfoQuestionKey.HeightWeight,
      hasAnswer:
        medicalInfo?.height !== null &&
        medicalInfo?.heightUnits !== null &&
        medicalInfo?.weight !== null &&
        medicalInfo?.weightUnits !== null,
      question: "Height & weight",
      answer: (
        <HeightWeight
          height={medicalInfo?.height}
          heightUnits={medicalInfo?.heightUnits}
          weight={medicalInfo?.weight}
          weightUnits={medicalInfo?.weightUnits}
        />
      )
    },
    {
      questionKey: MedicalInfoQuestionKey.MentalHealthScreener,
      hasAnswer: hasMentalHealthScreenerAnswer(medicalInfo),
      question: MENTAL_HEALTH_PREFIX,
      answer: <MentalHealthScreenerAnswers medicalInfo={medicalInfo} />
    }
  ]
}

export function getAllMedicalQuestions(medicalInfo: MedicalInfo): RoutingInfoQuestion[] {
  return [
    ...getAfabOnlyMedicalQuestions(medicalInfo),
    ...getAmabOnlyMedicalQuestions(medicalInfo),
    ...getSharedMedicalQuestions(medicalInfo),
    ...getPregnancyMedicalQuestions(medicalInfo),
    ...getPostpartumMedicalQuestions(medicalInfo)
  ]
}

function answerKeysFrom(questions: RoutingInfoQuestion[]): Set<string> {
  return new Set(questions.flatMap((q) => (q.answerKey != null ? [q.answerKey as string] : [])))
}

/** Sentinel value so list builders return stable question keys without real member data. */
const EMPTY_MEDICAL_INFO = (null as unknown) as MedicalInfo

export const AFAB_ONLY_ANSWER_KEYS = answerKeysFrom(getAfabOnlyMedicalQuestions(EMPTY_MEDICAL_INFO))
export const AMAB_ONLY_ANSWER_KEYS = answerKeysFrom(getAmabOnlyMedicalQuestions(EMPTY_MEDICAL_INFO))
export const PREGNANCY_ANSWER_KEYS = answerKeysFrom(getPregnancyMedicalQuestions(EMPTY_MEDICAL_INFO))
export const POSTPARTUM_ANSWER_KEYS = answerKeysFrom(getPostpartumMedicalQuestions(EMPTY_MEDICAL_INFO))
